import React, { useCallback, useEffect, useState } from "react";
import { FiActivity, FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { listPaymentConfirmationEvents } from "../../service/CallCenterPaymentConfirmationConfig";
import ListingEmptyState from "./listing/ListingEmptyState";
import ListingPaginationFooter from "./listing/ListingPaginationFooter";
import StatusBadge from "./listing/StatusBadge";
import TableSkeleton from "./listing/TableSkeleton";
import {
  LISTING_CARD_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "./listing/listingStyles";

const PAGE_SIZE = 10;

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

const resolveEventStatus = (event, t) => {
  if (event.processed) {
    return { label: t("ccPaymentConfirmationEventProcessed"), status: "ACTIVE" };
  }
  if (event.errorMessage) {
    return { label: t("ccPaymentConfirmationEventFailed"), status: "ESCALATED_L1" };
  }
  return { label: t("ccPaymentConfirmationEventSkipped"), status: "WARNING" };
};

const PaymentConfirmationEventLogPanel = ({ refreshKey = 0 }) => {
  const t = useCallCenterT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const totalPages = Math.max(Math.ceil(totalElements / PAGE_SIZE), totalElements > 0 ? 1 : 0);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const response = await listPaymentConfirmationEvents({ page, size: PAGE_SIZE });
      setItems(Array.isArray(response?.items) ? response.items : []);
      setTotalElements(Number(response?.total) || 0);
    } catch (error) {
      const message = error?.message || t("ccPaymentConfirmationEventLogLoadFailed");
      setLoadError(message);
      setItems([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, refreshKey]);

  return (
    <div className="mt-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {t("ccPaymentConfirmationEventLogTitle")}
          </h2>
          <p className="mt-1 text-sm text-slate-500">{t("ccPaymentConfirmationEventLogSubtitle")}</p>
        </div>
        <button
          type="button"
          onClick={loadEvents}
          disabled={loading}
          className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {t("ccRefresh")}
        </button>
      </div>

      {loadError ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{loadError}</span>
          <button type="button" onClick={loadEvents} className="font-semibold hover:underline">
            {t("ccRefresh")}
          </button>
        </div>
      ) : null}

      <div className={LISTING_CARD_CLASS}>
        {loading && !items.length ? (
          <div className="p-4">
            <TableSkeleton columns={6} rows={5} />
          </div>
        ) : !items.length ? (
          <ListingEmptyState
            icon={FiActivity}
            title={t("ccPaymentConfirmationEventLogEmpty")}
            hint={t("ccPaymentConfirmationEventLogEmptyHint")}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={LISTING_TABLE_HEAD_CLASS}>
                  <tr>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {t("ccPaymentConfirmationEventCreatedAt")}
                    </th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {t("ccPaymentConfirmationEventAccountNo")}
                    </th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {t("ccPaymentConfirmationEventReference")}
                    </th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {t("ccPaymentConfirmationEventProvider")}
                    </th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>{t("ccStatus")}</th>
                    <th className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {t("ccPaymentConfirmationEventError")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((event) => {
                    const status = resolveEventStatus(event, t);
                    return (
                      <tr key={event.id} className={LISTING_TABLE_ROW_CLASS}>
                        <td className="px-4 py-3 text-slate-600">{formatTimestamp(event.createdAt)}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">
                          {event.accountNo || t("ccEmDash")}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {event.externalReference || t("ccEmDash")}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{event.provider || t("ccEmDash")}</td>
                        <td className="px-4 py-3">
                          <StatusBadge status={status.status} label={status.label} />
                        </td>
                        <td className="max-w-xs px-4 py-3 text-slate-500">
                          {event.errorMessage || t("ccEmDash")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ListingPaginationFooter
              totalElements={totalElements}
              resultsLabel={t("ccPaymentConfirmationEventResultsLabel")}
              page={page}
              totalPages={totalPages}
              loading={loading}
              onPrevious={() => setPage((value) => Math.max(0, value - 1))}
              onNext={() => setPage((value) => value + 1)}
              previousLabel={t("ccPrevious")}
              nextLabel={t("ccNext")}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentConfirmationEventLogPanel;
